import { usePublicTestimonials } from '../../../hooks/unified/useTestimonialsQueries';
import { useViewport } from '../../../hooks/useViewport';
import { SwipeableCarousel } from '../../../components/ui/SwipeableCarousel';
import { env } from '../../../config/env';

// Helper to get full avatar URL
const getAvatarUrl = (url: string | undefined): string | undefined => {
  if (!url) return undefined;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `${env.API_BASE_URL}${url}`;
};

export function TestimonialsSection() {
  const { data: testimonials = [], isLoading } = usePublicTestimonials();
  const { isMobile, prefersReducedMotion } = useViewport();

  if (isLoading) {
    return (
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse text-center">
            <div className="h-6 sm:h-8 bg-gray-200 rounded w-1/3 mx-auto mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3 mx-auto"></div>
          </div>
        </div>
      </section>
    );
  }

  if (testimonials.length === 0) {
    return null;
  }

  const transitionClasses = prefersReducedMotion ? '' : 'transition-all duration-500';

  // Create carousel items from testimonials
  const carouselItems = testimonials.map((testimonial) => (
    <div key={testimonial.id} className="px-4 sm:px-0">
      <div className="bg-gradient-to-br from-primary-50 to-blue-50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 relative">
        {/* Quote Icon */}
        <div className="absolute top-4 sm:top-8 left-4 sm:left-8 text-primary-200">
          <svg className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16" fill="currentColor" viewBox="0 0 24 24">
            <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
          </svg>
        </div>

        <div className="relative pt-8 sm:pt-4">
          <blockquote className="text-base sm:text-lg lg:text-xl xl:text-2xl text-gray-700 leading-relaxed mb-6 sm:mb-8 text-center">
            "{testimonial.content}"
          </blockquote>

          <div className="flex flex-col items-center">
            {testimonial.user_avatar ? (
              <img 
                src={getAvatarUrl(testimonial.user_avatar)} 
                alt={testimonial.user_name}
                className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-full object-cover mb-3 sm:mb-4 ring-4 ring-white shadow-lg"
              />
            ) : (
              <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white text-lg sm:text-xl font-bold mb-3 sm:mb-4 ring-4 ring-white shadow-lg">
                {testimonial.user_name[0].toUpperCase()}
              </div>
            )}
            <div className="text-center">
              <div className="font-semibold text-gray-900 text-sm sm:text-base">{testimonial.user_name}</div>
              <div className="text-gray-500 text-xs sm:text-sm">
                {testimonial.position || 'Professional'} 
                {testimonial.company && ` at ${testimonial.company}`}
              </div>
            </div>

            {/* Rating Stars */}
            {testimonial.rating && (
              <div className="flex items-center gap-0.5 sm:gap-1 mt-2 sm:mt-3">
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg
                    key={star}
                    className={`w-4 h-4 sm:w-5 sm:h-5 ${
                      star <= testimonial.rating! ? 'text-yellow-400' : 'text-gray-300'
                    }`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  ));

  return (
    <section className="py-16 sm:py-20 lg:py-24 bg-white overflow-hidden mobile-overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-12 lg:mb-16">
          <span className="inline-block px-3 sm:px-4 py-1 bg-primary-100 text-primary-700 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4">
            Testimonials
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
            Success Stories
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto mobile-px">
            Hear from professionals who found their dream careers through our platform.
          </p>
        </div>

        {/* Featured Testimonial Carousel */}
        <div className="max-w-4xl mx-auto">
          <SwipeableCarousel
            items={carouselItems}
            showDots={true}
            showArrows={!isMobile}
            autoPlay={!prefersReducedMotion}
            interval={5000}
          />
        </div>

        {/* Testimonials Grid - Hidden on mobile for cleaner layout */}
        {testimonials.length > 1 && !isMobile && (
          <div className="mt-16 sm:mt-20">
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 text-center mb-6 sm:mb-8">
              More Success Stories
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {testimonials.slice(0, 3).map((testimonial) => (
                <div 
                  key={`grid-${testimonial.id}`}
                  className={`bg-gray-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 hover:shadow-lg ${transitionClasses}`}
                >
                  <div className="flex items-center gap-3 mb-3 sm:mb-4">
                    {testimonial.user_avatar ? (
                      <img 
                        src={getAvatarUrl(testimonial.user_avatar)} 
                        alt={testimonial.user_name}
                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white font-bold text-sm sm:text-base">
                        {testimonial.user_name[0].toUpperCase()}
                      </div>
                    )}
                    <div>
                      <div className="font-semibold text-gray-900 text-sm sm:text-base">{testimonial.user_name}</div>
                      <div className="text-gray-500 text-xs sm:text-sm">
                        {testimonial.position || 'Professional'}
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm sm:text-base line-clamp-4">"{testimonial.content}"</p>
                  {testimonial.rating && (
                    <div className="flex items-center gap-0.5 sm:gap-1 mt-3 sm:mt-4">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <svg
                          key={star}
                          className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${
                            star <= testimonial.rating! ? 'text-yellow-400' : 'text-gray-300'
                          }`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
